import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Building2, Users, Wrench, ChevronRight, ArrowLeft, ShieldAlert, Wallet } from "lucide-react";
import { InvestmentProperty, fmt } from "@/hooks/useREInvestingSim";
import { getPropertyImage } from "./propertyImages";
import { getRealm } from "@/components/lunimar/lunimarRealms";
import { ScenarioBrief } from "@/components/lunimar-kit";

interface Props {
  properties: InvestmentProperty[];
  availableCash: number;
  onAnalyze: (property: InvestmentProperty) => void;
  onBack?: () => void;
}

const RISK_HSL: Record<string, string> = {
  low: "152 70% 55%",
  "low-moderate": "152 70% 55%",
  moderate: "38 95% 60%",
  "moderate-high": "24 92% 60%",
  high: "0 78% 60%",
};

const CONDITION_HSL: Record<string, string> = {
  great: "152 70% 55%",
  good: "188 88% 58%",
  fair: "38 95% 60%",
  poor: "0 78% 60%",
};

function RiskBadge({ level }: { level: string }) {
  const hsl = RISK_HSL[level] || "0 0% 100%";
  return (
    <span
      className="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider capitalize"
      style={{
        borderColor: `hsl(${hsl} / 0.5)`,
        background: `hsl(${hsl} / 0.12)`,
        color: `hsl(${hsl})`,
      }}
    >
      <ShieldAlert className="h-2.5 w-2.5" /> {level}
    </span>
  );
}

export default function PropertyMarketplace({ properties, availableCash, onAnalyze, onBack }: Props) {
  const realm = getRealm("re_investing");
  const [filter, setFilter] = useState<string | null>(null);

  const filtered = filter ? properties.filter(p => p.city === filter) : properties;
  const cities = [...new Set(properties.map(p => p.city))];

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        {onBack && (
          <Button variant="ghost" size="icon" onClick={onBack} className="mt-1">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        )}
        <div className="flex-1">
          <ScenarioBrief
            realm={realm}
            eyebrow="Skyline Holdings · Marketplace"
            title="Live deal feed"
            intent="Each listing has its own cash-flow story, risk profile, and capital pressure. Run the analyzer before committing — three things will sink a deal: wrong price, weak rent, hidden repairs."
          >
            <div
              className="mt-2 inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 font-mono text-xs"
              style={{
                borderColor: `hsl(${realm.primaryAccent} / 0.4)`,
                background: `hsl(${realm.primaryAccent} / 0.10)`,
                color: `hsl(${realm.primaryAccent})`,
              }}
            >
              <Wallet className="h-3.5 w-3.5" /> Cash available: {fmt(availableCash)}
            </div>
          </ScenarioBrief>
        </div>
      </div>

      {/* City filter */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setFilter(null)}
          className="rounded-full border px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider transition-all"
          style={{
            borderColor: !filter ? `hsl(${realm.primaryAccent} / 0.6)` : "hsl(0 0% 100% / 0.15)",
            background: !filter ? `hsl(${realm.primaryAccent} / 0.12)` : "transparent",
            color: !filter ? `hsl(${realm.primaryAccent})` : "hsl(0 0% 100% / 0.6)",
          }}
        >
          All cities
        </button>
        {cities.map(city => (
          <button
            key={city}
            onClick={() => setFilter(city)}
            className="rounded-full border px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider transition-all"
            style={{
              borderColor: filter === city ? `hsl(${realm.primaryAccent} / 0.6)` : "hsl(0 0% 100% / 0.15)",
              background: filter === city ? `hsl(${realm.primaryAccent} / 0.12)` : "transparent",
              color: filter === city ? `hsl(${realm.primaryAccent})` : "hsl(0 0% 100% / 0.6)",
            }}
          >
            {city}
          </button>
        ))}
      </div>

      {/* Property cards */}
      <div className="grid gap-4">
        {filtered.map((prop) => {
          const canAfford = prop.purchase_price * 0.03 + prop.purchase_price * 0.03 <= availableCash; // 3% DP + 3% closing
          const img = getPropertyImage(prop.property_key);
          const condHsl = CONDITION_HSL[prop.condition_score] || "0 0% 100%";
          return (
            <div
              key={prop.id}
              className="overflow-hidden rounded-2xl border backdrop-blur-md transition-all"
              style={{
                borderColor: "hsl(0 0% 100% / 0.10)",
                background: "hsl(228 45% 12% / 0.55)",
                boxShadow: "0 18px 40px -28px hsl(230 60% 4% / 0.7)",
              }}
            >
              {img && (
                <div className="relative h-36 w-full overflow-hidden">
                  <img src={img} alt={prop.property_name} className="h-full w-full object-cover" />
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0"
                    style={{
                      background: `linear-gradient(180deg, transparent 40%, hsl(228 45% 8% / 0.85))`,
                    }}
                  />
                </div>
              )}
              <div className="space-y-3 p-4 sm:p-5">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3">
                  <div
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border"
                    style={{
                      borderColor: `hsl(${realm.primaryAccent} / 0.35)`,
                      background: `hsl(${realm.primaryAccent} / 0.10)`,
                      color: `hsl(${realm.primaryAccent})`,
                    }}
                  >
                    <Building2 className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-heading text-sm font-bold text-white">{prop.property_name}</p>
                    <p className="text-[11px] font-mono uppercase tracking-wider text-white/55">{prop.city} · {prop.property_type}</p>
                  </div>
                </div>
                <RiskBadge level={prop.risk_level} />
              </div>

              <div className="grid grid-cols-4 gap-2">
                {[
                  { label: "Price", value: fmt(prop.purchase_price), hsl: realm.primaryAccent },
                  { label: "Rent", value: `${fmt(prop.estimated_rent)}/mo`, hsl: "152 70% 55%" },
                  { label: "Condition", value: prop.condition_score, hsl: condHsl, capitalize: true },
                  { label: "Appreciation", value: prop.appreciation_rating, hsl: realm.secondaryAccent, capitalize: true },
                ].map(s => (
                  <div
                    key={s.label}
                    className="rounded-lg border p-2 text-center"
                    style={{
                      borderColor: `hsl(${s.hsl} / 0.25)`,
                      background: `hsl(${s.hsl} / 0.08)`,
                    }}
                  >
                    <p className="text-[10px] font-mono uppercase tracking-wider text-white/55">{s.label}</p>
                    <p
                      className={`mt-0.5 font-heading text-[11px] font-bold tabular-nums ${s.capitalize ? "capitalize" : ""}`}
                      style={{ color: `hsl(${s.hsl})` }}
                    >
                      {s.value}
                    </p>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between gap-2">
                <div className="flex flex-wrap gap-3 text-[11px] text-white/65">
                  <Tooltip>
                    <TooltipTrigger className="flex items-center gap-1"><Users className="w-3 h-3" /> {prop.tenant_demand}</TooltipTrigger>
                    <TooltipContent>Tenant demand level</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger className="flex items-center gap-1"><Wrench className="w-3 h-3" /> {prop.maintenance_risk}</TooltipTrigger>
                    <TooltipContent>Maintenance risk level</TooltipContent>
                  </Tooltip>
                  {prop.hoa_monthly > 0 && (
                    <Tooltip>
                      <TooltipTrigger className="flex items-center gap-1">HOA {fmt(prop.hoa_monthly)}/mo</TooltipTrigger>
                      <TooltipContent>Monthly HOA fees reduce cash flow</TooltipContent>
                    </Tooltip>
                  )}
                </div>
                <Button
                  size="sm"
                  onClick={() => onAnalyze(prop)}
                  disabled={!canAfford}
                  className="gap-1"
                >
                  {canAfford ? "Analyze Deal" : "Can't Afford"}
                  {canAfford && <ChevronRight className="w-3.5 h-3.5" />}
                </Button>
              </div>

              {prop.notes && <p className="text-[11px] italic text-white/55">{prop.notes}</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
